import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const ASSETS = `${API}/assets`;

// Tokens kept in memory + mirrored to localStorage for refresh-on-reload
let _access = localStorage.getItem("chillet_access") || null;
let _refresh = localStorage.getItem("chillet_refresh") || null;

export function setTokens(access, refresh) {
  _access = access;
  _refresh = refresh;
  if (access) localStorage.setItem("chillet_access", access); else localStorage.removeItem("chillet_access");
  if (refresh) localStorage.setItem("chillet_refresh", refresh); else localStorage.removeItem("chillet_refresh");
}
export function getAccess() { return _access; }
export function getRefresh() { return _refresh; }
export function clearTokens() { setTokens(null, null); }

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  if (_access) config.headers.Authorization = `Bearer ${_access}`;
  return config;
});

let refreshing = null;

// ---------------------------------------------------------------------------
// Global failure surfacing
// ---------------------------------------------------------------------------
// Components handle their own 4xx (they know the context — "insufficient
// coins", "not linked", …). What used to die silently was the class of
// failure nobody writes a catch branch for: the backend being down, a
// Cloudflare 5xx, a dropped connection. Those now always raise a toast.
//
// Deliberately narrow so we don't double-toast: only no-response errors and
// 5xx, deduped for 4s on identical text, and skippable per call with
// `api.get(url, { silent: true })`.
let _toastSink = null;
export function setToastSink(fn) { _toastSink = fn; }

const _recent = new Map();

function surfaceGlobalError(error) {
  const cfg = error?.config || {};
  if (cfg.silent) return;
  const status = error.response?.status;
  const isNetwork = !error.response;
  if (!isNetwork && status < 500) return;
  const msg = isNetwork
    ? "Can't reach the server — check your connection and try again."
    : `Server error (${status}). Please try again in a moment.`;
  const now = Date.now();
  if (_recent.get(msg) && now - _recent.get(msg) < 4000) return;
  _recent.set(msg, now);
  if (_toastSink) _toastSink(msg);
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retried &&
      _refresh &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/register") &&
      !original.url?.includes("/auth/admin/login")
    ) {
      original._retried = true;
      try {
        if (!refreshing) {
          refreshing = axios.post(`${API}/auth/refresh`, { refresh_token: _refresh });
        }
        const resp = await refreshing;
        refreshing = null;
        setTokens(resp.data.access_token, resp.data.refresh_token);
        original.headers.Authorization = `Bearer ${resp.data.access_token}`;
        return api(original);
      } catch (e) {
        refreshing = null;
        clearTokens();
        if (typeof window !== "undefined") window.location.href = "/login";
        throw e;
      }
    }
    surfaceGlobalError(error);
    throw error;
  }
);

export function apiError(e, fallback = "Something went wrong") {
  // Axios → FastAPI: `e.response.data.detail` is usually a string, but
  // some endpoints (cooldowns, validation) return a structured object
  // like `{message, expires_at}` or FastAPI's `[{loc, msg, type}, ...]`.
  // Always return a STRING so callers can pass straight to `toast.error`
  // / inline-render without React choking on "Objects are not valid as
  // a React child."
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (Array.isArray(detail)) {
      const first = detail[0];
      if (first?.msg) return String(first.msg);
    }
    // Last-ditch: serialise so the user sees SOMETHING actionable
    // rather than the page crashing.
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }
  return e?.message || fallback;
}

// ---------------------------------------------------------------------------
// getShared — one request per URL per TTL, shared across components
// ---------------------------------------------------------------------------
// `/servers/status` is polled independently by the home strip, the dashboard
// and the online-players pill, so the logs showed the same URL fetched four
// times within a second. This collapses concurrent callers onto one in-flight
// request and serves a short-lived cached body to the rest.
const _shared = new Map();  // url → { at, data, inflight }

export async function getShared(url, ttlMs = 20000) {
  const now = Date.now();
  const hit = _shared.get(url);
  if (hit?.inflight) return hit.inflight;
  if (hit && now - hit.at < ttlMs) return hit.data;
  const inflight = api.get(url)
    .then(({ data }) => {
      _shared.set(url, { at: Date.now(), data });
      return data;
    })
    .catch((e) => {
      _shared.delete(url);
      throw e;
    });
  _shared.set(url, { ...(hit || {}), inflight });
  return inflight;
}
